const BLOOMS_LEVELS = new Set([
  'remember',
  'understand',
  'apply',
  'analyze',
  'evaluate',
  'create',
]);

const toTrimmedString = (value, fallback = '') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const toStringArray = (value, { min = 0, max = Number.POSITIVE_INFINITY } = {}) => {
  if (!Array.isArray(value)) return [];
  const cleaned = value
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
  return cleaned.slice(0, max < min ? min : max);
};

const clampArray = (value, min, max, fallbackFactory) => {
  const cleaned = toStringArray(value, { max });
  if (cleaned.length >= min) return cleaned;
  return fallbackFactory().slice(0, max);
};

const toBlueprintLesson = (rawLesson, breakdownItem) => {
  const lessonIndex = Number(breakdownItem?.lessonIndex) || 1;
  const targetWords = Number(breakdownItem?.targetWords) || 0;
  const isFinalAssessment = Boolean(breakdownItem?.isFinalAssessment);
  const topicOutline = clampArray(
    rawLesson?.topicOutline,
    3,
    7,
    () => [toTrimmedString(rawLesson?.title, `Lesson ${lessonIndex}`)]
  );
  const keyVocabulary = clampArray(
    rawLesson?.keyVocabulary,
    3,
    8,
    () => topicOutline.slice(0, 3)
  );
  const bloomsRaw = toTrimmedString(rawLesson?.bloomsLevel, isFinalAssessment ? 'evaluate' : 'understand').toLowerCase();
  const bloomsLevel = BLOOMS_LEVELS.has(bloomsRaw) ? bloomsRaw : 'understand';
  return {
    lessonIndex,
    title: toTrimmedString(rawLesson?.title, `Lesson ${lessonIndex}`),
    isFinalAssessment,
    targetWords,
    topicOutline,
    contentDepthGuidance: toTrimmedString(
      rawLesson?.contentDepthGuidance,
      isFinalAssessment
        ? 'Assessment period only: summarize prior learning and do not introduce new content.'
        : 'Build conceptual depth from prior lessons without introducing unrelated topics.'
    ),
    priorKnowledgeAssumptions: toTrimmedString(
      rawLesson?.priorKnowledgeAssumptions,
      isFinalAssessment
        ? 'Learner completed the full course and needs concise review cues.'
        : 'Assume only concepts covered in earlier lessons.'
    ),
    bridgeToNext: toTrimmedString(
      rawLesson?.bridgeToNext,
      isFinalAssessment ? 'Bridge to assessment readiness and recap strategy.' : 'Preview the next lesson concept thread.'
    ),
    keyVocabulary,
    bloomsLevel,
  };
};

export function normalizeBlueprintPayload(rawBlueprint, lessonSizing) {
  const breakdown = Array.isArray(lessonSizing?.breakdown) ? lessonSizing.breakdown : [];
  const rawLessons = Array.isArray(rawBlueprint?.lessons) ? rawBlueprint.lessons : [];
  const lessons = breakdown.map((entry, index) => toBlueprintLesson(rawLessons[index] || {}, entry));
  const actualDistributedWords = lessons.reduce((sum, lesson) => sum + (Number(lesson.targetWords) || 0), 0);
  return {
    actualDistributedWords,
    lessons,
  };
}

export function deriveSyllabusFromBlueprint(blueprint) {
  const lessons = Array.isArray(blueprint?.lessons) ? blueprint.lessons : [];
  return lessons.map((lesson, idx) => {
    const day = Number(lesson?.lessonIndex) || idx + 1;
    const topicOutline = toStringArray(lesson?.topicOutline, { max: 7 });
    const keyVocabulary = toStringArray(lesson?.keyVocabulary, { max: 8 });
    return {
      day,
      title: toTrimmedString(lesson?.title, `Day ${day}`),
      outline: topicOutline.join('; '),
      objectives: topicOutline,
      keyConcepts: keyVocabulary,
      keyConceptsPreview: keyVocabulary.slice(0, 4),
      bloomTarget: toTrimmedString(lesson?.bloomsLevel, 'understand'),
    };
  });
}

export function countWordsInNarration(text) {
  return toTrimmedString(text)
    .split(/\s+/)
    .filter(Boolean)
    .length;
}
